/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ReduxUtils } from '@spm/core';
import { combineReducers } from 'redux';
import * as ACTIONTYPES from './actionTypes';

/**
 * Updates the submission form details for the 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function getSubmissionFormDetailsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.GET_SUBMISSION_FORM_DETAILS_ACTION_TYPES);
}

/**
 * Updates the indicator that specifies whether submission form details are being retrieved or not,
 * for the 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isGettingSubmissionFormDetailsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.GET_SUBMISSION_FORM_DETAILS_ACTION_TYPES);
}

/**
 * Updates the 'UASubmissionForm' store object with any errors that may have occurred when getting
 * the submission page details.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function getSubmissionFormDetailsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.GET_SUBMISSION_FORM_DETAILS_ACTION_TYPES);
}

/**
 * Updates the indicator that specifies whether submission form is being created or not, for the
 * 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isCreatingSubmissionFormReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_CREATE_SUBMISSION_FORM_ACTION_TYPES);
}

/**
 * Updates the 'UASubmissionForm' store object with any errors that may have occurred when creating
 * the submission form.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function createSubmissionFormErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_CREATE_SUBMISSION_FORM_ACTION_TYPES);
}

/**
 * Updates the current page details for the 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function currentPageDetailsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_GET_CURRENT_PAGE_DETAILS_ACTION_TYPES);
}

/**
 * Updates the indicator that specifies whether current page details are being retrieved or not, for
 * the 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isGettingCurrentPageDetailsReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_GET_CURRENT_PAGE_DETAILS_ACTION_TYPES);
}

/**
 * Updates the 'UASubmissionForm' store object with any errors that may have occurred when getting
 * the current page details.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function getCurrentPageDetailsErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_GET_CURRENT_PAGE_DETAILS_ACTION_TYPES);
}

/**
 * Updates the indicator that specifies whether current page answers are being sent or not, for the
 * 'UASubmissionForm' store object.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function isSendingCurrentPageAnswersReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateFetchingReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_SEND_CURRENT_PAGE_ANSWERS_ACTION_TYPES);
}

/**
 * Updates the 'UASubmissionForm' store object with any errors that may have occurred when sending
 * the current page answers.
 *
 * @param {any} [state={}] the initial state for the reducer
 * @param {any} action the action creator
 * @returns the new state
 */
function sendCurrentPageAnswersErrorReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  return ReduxUtils.generateErrorReducer(state, action, ACTIONTYPES.SUBMISSION_FORM_SEND_CURRENT_PAGE_ANSWERS_ACTION_TYPES);
}
var reducers = combineReducers({
  getSubmissionFormDetails: getSubmissionFormDetailsReducer,
  isGettingSubmissionFormDetails: isGettingSubmissionFormDetailsReducer,
  getSubmissionFormDetailsError: getSubmissionFormDetailsErrorReducer,
  isCreatingSubmissionForm: isCreatingSubmissionFormReducer,
  createSubmissionFormError: createSubmissionFormErrorReducer,
  currentPageDetails: currentPageDetailsReducer,
  isGettingCurrentPageDetails: isGettingCurrentPageDetailsReducer,
  getCurrentPageDetailsError: getCurrentPageDetailsErrorReducer,
  isSendingCurrentPageAnswers: isSendingCurrentPageAnswersReducer,
  sendCurrentPageAnswersError: sendCurrentPageAnswersErrorReducer
});
export default {
  reducers: reducers
};